import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, ChevronRightIcon, CheckCircle2Icon, WrenchIcon } from 'lucide-react';
import { states } from '../data/states';
import { services } from '../data/services';
interface StatesDirectoryProps {
  mode: 'list' | 'detail';
  stateCode?: string;
  onNavigate: (path: string) => void;
}
export function StatesDirectory({
  mode,
  stateCode,
  onNavigate
}: StatesDirectoryProps) {
  const [zipCode, setZipCode] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [isValidZip, setIsValidZip] = useState(false);
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZipCode(val);
    setIsValidZip(val.length === 5);
  };
  const handleContinue = () => {
    if (isValidZip && selectedService) {
      onNavigate(`/${selectedService}/quote-form?zip=${zipCode}`);
    }
  };
  if (mode === 'detail' && stateCode) {
    const stateData = states.find(s => s.slug === stateCode.toLowerCase());
    if (!stateData) {
      return <div className="py-20 text-center" data-id="element-2881">State not found</div>;
    }
    // Use a high-quality home exterior image for the state pages
    const heroImage = 'https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=1600&q=80';
    return <div className="min-h-screen bg-white" data-id="element-2882">
        {/* Breadcrumb */}
        <div className="bg-white py-4 border-b border-gray-100" data-id="element-2883">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center text-sm text-gray-500" data-id="element-2884">
            <button onClick={() => onNavigate('/')} className="hover:text-amber-600 transition-colors" data-id="element-2885">
              Home
            </button>
            <ChevronRightIcon className="w-4 h-4 mx-2" data-id="element-2886" />
            <button onClick={() => onNavigate('/states')} className="hover:text-amber-600 transition-colors" data-id="element-2887">
              States
            </button>
            <ChevronRightIcon className="w-4 h-4 mx-2" data-id="element-2888" />
            <span className="text-gray-900 font-medium" data-id="element-2889">{stateData.name}</span>
          </div>
        </div>

        {/* Hero Section */}
        <section className="relative py-20 md:py-32 overflow-hidden" data-id="element-2890">
          {/* Background Image with Overlay */}
          <div className="absolute inset-0 z-0" data-id="element-2891">
            <img src={heroImage} alt={`Home in ${stateData.name}`} className="w-full h-full object-cover" data-id="element-2892" />
            <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-[2px]" data-id="element-2893"></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-2894">
            <div className="flex flex-col md:flex-row items-center gap-12 lg:gap-20" data-id="element-2895">
              <div className="flex-1 text-center md:text-left" data-id="element-2896">
                <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.5
              }} data-id="element-2897">
                  <span className="inline-block py-1 px-3 rounded-full bg-amber-500/20 text-amber-300 text-sm font-bold tracking-wider uppercase mb-4 border border-amber-500/30" data-id="element-2898">
                    Local Pros in {stateData.abbreviation}
                  </span>
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight" data-id="element-2899">
                    Find Top-Rated Contractors in{' '}
                    <span className="text-amber-400" data-id="element-2900">{stateData.name}</span>
                  </h1>
                  <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-2xl mx-auto md:mx-0" data-id="element-2901">
                    Connect with vetted local professionals for your next home
                    improvement project. Get free, no-obligation quotes today.
                  </p>
                </motion.div>
              </div>

              <div className="w-full max-w-md" data-id="element-2902">
                <motion.div initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                duration: 0.5,
                delay: 0.2
              }} className="bg-white p-6 md:p-8 rounded-2xl shadow-2xl border border-slate-100" data-id="element-2903">
                  <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center" data-id="element-2904">
                    Get Free Quotes
                  </h3>

                  <div className="space-y-4" data-id="element-2905">
                    {/* Service Dropdown */}
                    <div data-id="element-2906">
                      <label htmlFor="service" className="block text-sm font-medium text-slate-700 mb-1" data-id="element-2907">
                        What service do you need?
                      </label>
                      <div className="relative" data-id="element-2908">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-2909">
                          <WrenchIcon className="h-5 w-5 text-slate-400" data-id="element-2910" />
                        </div>
                        <select id="service" value={selectedService} onChange={e => setSelectedService(e.target.value)} className="w-full h-14 pl-12 pr-10 text-base bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-colors appearance-none" data-id="element-2911">
                          <option value="" disabled data-id="element-2912">
                            Select a service...
                          </option>
                          <optgroup label="Bathroom" data-id="element-2913">
                            <option value="walk-in-tubs" data-id="element-2914">Walk-In Tubs</option>
                            <option value="bathroom-remodel" data-id="element-2915">
                              Bathroom Remodel
                            </option>
                          </optgroup>
                          <optgroup label="Exterior" data-id="element-2916">
                            <option value="roofing" data-id="element-2917">Roofing</option>
                            <option value="siding" data-id="element-2918">Siding</option>
                            <option value="window-replacement" data-id="element-2919">
                              Window Replacement
                            </option>
                            <option value="gutter-guards" data-id="element-2920">Gutter Guards</option>
                          </optgroup>
                          <optgroup label="Systems" data-id="element-2921">
                            <option value="hvac" data-id="element-2922">HVAC</option>
                            <option value="home-security" data-id="element-2923">Home Security</option>
                          </optgroup>
                          <optgroup label="Interior" data-id="element-2924">
                            <option value="flooring" data-id="element-2925">Flooring</option>
                          </optgroup>
                        </select>
                        <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none" data-id="element-2926">
                          <ChevronRightIcon className="h-5 w-5 text-slate-400 rotate-90" data-id="element-2927" />
                        </div>
                      </div>
                    </div>

                    {/* ZIP Code Input */}
                    <div data-id="element-2928">
                      <label htmlFor="zip" className="block text-sm font-medium text-slate-700 mb-1" data-id="element-2929">
                        Where are you located?
                      </label>
                      <div className="relative" data-id="element-2930">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-2931">
                          <MapPinIcon className="h-5 w-5 text-slate-400" data-id="element-2932" />
                        </div>
                        <input id="zip" type="text" value={zipCode} onChange={handleZipChange} placeholder="Enter ZIP Code" className="w-full h-14 pl-12 pr-12 text-base bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-colors" maxLength={5} data-id="element-2933" />
                        {isValidZip && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500" data-id="element-2934">
                            <CheckCircle2Icon className="w-6 h-6" data-id="element-2935" />
                          </div>}
                      </div>
                    </div>

                    <button onClick={handleContinue} disabled={!isValidZip || !selectedService} className={`w-full h-14 mt-4 rounded-xl font-bold text-lg text-white transition-all flex items-center justify-center gap-2 ${isValidZip && selectedService ? 'bg-amber-500 hover:bg-amber-600 shadow-lg shadow-amber-500/30' : 'bg-slate-300 cursor-not-allowed'}`} data-id="element-2936">
                      Find Local Pros <ChevronRightIcon className="w-5 h-5" data-id="element-2937" />
                    </button>
                    <p className="text-xs text-slate-500 text-center mt-4" data-id="element-2938">
                      Trusted by homeowners across {stateData.name}.
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Cities Grid */}
        <section className="py-20 bg-slate-50" data-id="element-2939">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2940">
            <div className="text-center mb-12" data-id="element-2941">
              <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-2942">
                Popular Cities in {stateData.name}
              </h2>
              <p className="text-lg text-slate-600" data-id="element-2943">
                We connect you with top-rated professionals in these areas and
                beyond.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 md:p-12" data-id="element-2944">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-y-8 gap-x-8" data-id="element-2945">
                {stateData.cities.map((city, idx) => <div key={idx} className="flex items-center gap-3 group" data-id="element-2946">
                    <div className="w-2 h-2 rounded-full bg-amber-400 group-hover:scale-150 transition-transform" data-id="element-2947"></div>
                    <span className="text-slate-700 font-medium group-hover:text-amber-600 transition-colors" data-id="element-2948">
                      {city}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </section>
      </div>;
  }
  // List Mode
  return <div className="min-h-screen bg-slate-50" data-id="element-2949">
      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-slate-200" data-id="element-2950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center text-sm text-slate-500" data-id="element-2951">
          <button onClick={() => onNavigate('/')} className="hover:text-amber-600 transition-colors" data-id="element-2952">
            Home
          </button>
          <ChevronRightIcon className="w-4 h-4 mx-2" data-id="element-2953" />
          <span className="text-slate-900 font-medium" data-id="element-2954">States</span>
        </div>
      </div>

      <section className="py-16 md:py-24" data-id="element-2955">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2956">
          <div className="text-center mb-16" data-id="element-2957">
            <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6" data-id="element-2958">
              Find Pros in Any State
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto" data-id="element-2959">
              Select your state below to find top-rated, vetted home improvement
              professionals in your local area.
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-8 md:p-12" data-id="element-2960">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-y-6 gap-x-8" data-id="element-2961">
              {states.map((state, idx) => <button key={idx} onClick={() => onNavigate(`/states/${state.slug}`)} className="text-left flex items-center justify-between group py-2 border-b border-slate-100 hover:border-amber-200 transition-colors" data-id="element-2962">
                  <span className="text-slate-700 font-medium group-hover:text-amber-600 transition-colors" data-id="element-2963">
                    {state.name}
                  </span>
                  <ChevronRightIcon className="w-4 h-4 text-slate-300 group-hover:text-amber-500 transition-colors transform group-hover:translate-x-1" data-id="element-2964" />
                </button>)}
            </div>
          </div>
        </div>
      </section>
    </div>;
}